// Import Session Service - IndexedDB for persistent storage
import { CSVColumn, ColumnMapping, ValidatedRow, ImportResult, ValidationError } from '../components/ImportParticipants/ImportWizard';

const DB_NAME = 'CountBubbleImportDB';
const DB_VERSION = 1;
const STORE_NAME = 'importSessions';

export type SessionStatus = 
    | 'uploaded' 
    | 'mapped' 
    | 'validated' 
    | 'importing' 
    | 'completed' 
    | 'failed';

export interface ImportSession {
    id: string;
    status: SessionStatus;
    createdAt: string;
    updatedAt: string;
    
    // File metadata
    fileName?: string;
    fileSize?: number;
    fileType?: string;
    rowCount?: number;
    
    // Step 1: File Upload
    csvData?: any[];
    csvColumns?: CSVColumn[];
    
    // Step 2: Column Mapping
    columnMappings?: ColumnMapping[];
    referenceDataCache?: any; // Cached reference data
    
    // Step 3: Validation
    validatedRows?: ValidatedRow[];
    validationErrors?: ValidationError[];
    
    // Step 4: Import Execution
    importProgress?: number;
    importResult?: ImportResult;
    currentRow?: number;
    
    // Current wizard step
    currentStep: number;

    completedGroupKeys?: string[];
    completedStandaloneIndices?: number[];
}

// Initialize IndexedDB
const initDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        
        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                objectStore.createIndex('status', 'status', { unique: false });
                objectStore.createIndex('createdAt', 'createdAt', { unique: false });
                objectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
            }
        };
    });
};

// Generate unique session ID
export const generateSessionId = (): string => {
    return `import_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Create new session
export const createSession = async (fileName: string, fileSize: number, fileType: string): Promise<string> => {
    const db = await initDB();
    const sessionId = generateSessionId();
    
    const session: ImportSession = {
        id: sessionId,
        status: 'uploaded',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        fileName,
        fileSize,
        fileType,
        currentStep: 1,
    };
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.add(session);
        
        request.onsuccess = () => resolve(sessionId);
        request.onerror = () => reject(request.error);
    });
};

// Get session by ID
export const getSession = async (sessionId: string): Promise<ImportSession | null> => {
    const db = await initDB();
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(sessionId);
        
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
    });
};

// Update session
export const updateSession = async (sessionId: string, updates: Partial<ImportSession>): Promise<void> => {
    const db = await initDB();
    const session = await getSession(sessionId);
    
    if (!session) {
        throw new Error(`Session ${sessionId} not found`);
    }
    
    const updatedSession: ImportSession = {
        ...session,
        ...updates,
        updatedAt: new Date().toISOString(),
    };
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(updatedSession);
        
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

// Get active session (most recent non-completed session)
export const getActiveSession = async (): Promise<ImportSession | null> => {
    const db = await initDB();
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('updatedAt');
        const request = index.openCursor(null, 'prev');
        
        request.onsuccess = () => {
            const cursor = request.result;
            if (cursor) {
                const session = cursor.value as ImportSession;
                // Return first session that is not completed or failed
                if (session.status !== 'completed' && session.status !== 'failed') {
                    resolve(session);
                } else {
                    cursor.continue();
                }
            } else {
                resolve(null);
            }
        };
        request.onerror = () => reject(request.error);
    });
};

// Get all sessions
export const getAllSessions = async (): Promise<ImportSession[]> => {
    const db = await initDB();
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();
        
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

// Delete session
export const deleteSession = async (sessionId: string): Promise<void> => {
    const db = await initDB();
    
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(sessionId);
        
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

// Clear old completed/failed sessions (older than 7 days)
export const clearOldSessions = async (): Promise<number> => {
    const db = await initDB();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const sessions = await getAllSessions();
    let deletedCount = 0;
    
    for (const session of sessions) {
        const sessionDate = new Date(session.updatedAt);
        if (
            (session.status === 'completed' || session.status === 'failed') &&
            sessionDate < sevenDaysAgo
        ) {
            await deleteSession(session.id);
            deletedCount++;
        }
    }
    
    return deletedCount;
};

// delete all sessions
export const deleteAllSessions = async (): Promise<number> => {
    const db = await initDB();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const sessions = await getAllSessions();
    let deletedCount = 0;
    
    for (const session of sessions) {
       await deleteSession(session.id);
       deletedCount++;
    }
    return deletedCount;
};

// Save CSV data to session
export const saveCsvData = async (
    sessionId: string, 
    csvData: any[], 
    csvColumns: CSVColumn[]
): Promise<void> => {
    await updateSession(sessionId, {
        csvData,
        csvColumns,
        rowCount: csvData.length,
        status: 'uploaded',
        currentStep: 1,
    });
};

// Save column mappings to session
export const saveColumnMappings = async (
    sessionId: string,
    columnMappings: ColumnMapping[],
    referenceData?: any
): Promise<void> => {
    await updateSession(sessionId, {
        columnMappings,
        referenceDataCache: referenceData,
        status: 'mapped',
        currentStep: 2,
    });
};

// Save validated rows to session
export const saveValidatedRows = async (
    sessionId: string,
    validatedRows: ValidatedRow[]
): Promise<void> => {
    const validationErrors = validatedRows
        .filter(row => !row.isValid)
        .flatMap(row => row.errors);
    
    await updateSession(sessionId, {
        validatedRows,
        validationErrors,
        status: 'validated',
        currentStep: 3,
    });
};

// Update import progress
export const updateImportProgress = async (
    sessionId: string,
    progress: number,
    currentRow: number
): Promise<void> => {
    await updateSession(sessionId, {
        importProgress: progress,
        currentRow,
        status: 'importing',
        currentStep: 4,
    });
};

// Save import result
export const saveImportResult = async (
    sessionId: string,
    importResult: ImportResult
): Promise<void> => {
    const status: SessionStatus = importResult.failedCount > 0 ? 'failed' : 'completed';
    
    await updateSession(sessionId, {
        importResult,
        importProgress: 100,
        status,
        currentStep: 4,
    });
};

// Mark session as failed
export const markSessionFailed = async (sessionId: string, error?: string): Promise<void> => {
    await updateSession(sessionId, {
        status: 'failed',
    });
};

// Update current step
export const updateCurrentStep = async (sessionId: string, step: number): Promise<void> => {
    await updateSession(sessionId, {
        currentStep: step,
    });
};

export const saveCompletedUnit = async (
    sessionId: string,
    unit: { groupKey?: string; standaloneIndex?: number }
): Promise<void> => {
    const session = await getSession(sessionId);
    if (!session) return;

    const completedGroupKeys = session.completedGroupKeys || [];
    const completedStandaloneIndices = session.completedStandaloneIndices || [];

    if (unit.groupKey) {
        completedGroupKeys.push(unit.groupKey);
    }
    if (unit.standaloneIndex !== undefined) {
        completedStandaloneIndices.push(unit.standaloneIndex);
    }

    await updateSession(sessionId, {
        completedGroupKeys,
        completedStandaloneIndices,
    });
};